
import React, { useState } from 'react';
import { Rental } from '@/services/types';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { RentalDetailsCard } from './RentalDetailsCard';
import { RentalPaymentsTab } from './tabs/RentalPaymentsTab';
import { RentalSubscriptionsTab } from './tabs/RentalSubscriptionsTab';
import { RentalMileageTab } from './tabs/RentalMileageTab';

interface RentalDetailsDialogProps {
  rental: Rental;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: string) => void;
  onRentalUpdate?: () => void; // Проп для обновления после действий с подпиской
  loading: boolean;
}

export const RentalDetailsDialog: React.FC<RentalDetailsDialogProps> = ({
  rental,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onStatusChange,
  onRentalUpdate,
  loading
}) => {
  const [activeTab, setActiveTab] = useState('details');
  
  // Проверка наличия объекта rental
  if (!rental && open) {
    console.warn('RentalDetailsDialog: rental объект отсутствует, закрытие диалога');
    onOpenChange(false);
    return null;
  }

  // Оптимизированный обработчик изменения видимости диалога
  const handleOpenChange = (open: boolean) => {
    // Предотвращаем множественные открытия/закрытия
    if (!open) {
      // При закрытии вызываем onOpenChange с небольшой задержкой
      // чтобы избежать мерцания при обновлении данных
      setTimeout(() => {
        onOpenChange(false);
      }, 100);
    } else {
      onOpenChange(true);
    }
  };

  // Обработчик для обновления данных после изменений подписки
  const handleTabUpdate = () => {
    if (onRentalUpdate) {
      onRentalUpdate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[900px] p-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Детали аренды</DialogTitle>
        </DialogHeader>
        {rental && (
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="w-full"
          >
            <div className="border-b px-6 py-2">
              <TabsList className="grid grid-cols-4 w-full md:max-w-lg">
                <TabsTrigger value="details">
                  Детали
                </TabsTrigger>
                <TabsTrigger value="mileage">
                  Пробег
                </TabsTrigger>
                <TabsTrigger value="payments">
                  Платежи
                </TabsTrigger>
                <TabsTrigger value="subscriptions">
                  Подписки {rental.subscription_id && (
                    <Badge variant="secondary" className="ml-1">1</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="details" className="m-0">
              <RentalDetailsCard 
                rental={rental}
                loading={loading}
                onStatusChange={onStatusChange}
                onEdit={() => {
                  onOpenChange(false);
                  onEdit();
                }}
                onClose={() => onOpenChange(false)}
                onDelete={onDelete}
                onRentalUpdate={onRentalUpdate}
              />
            </TabsContent>
            
            <TabsContent value="mileage" className="m-0 p-4">
              <RentalMileageTab 
                rentalId={rental.id}
              />
            </TabsContent>
            
            <TabsContent value="payments" className="m-0 p-4">
              <RentalPaymentsTab 
                rentalId={rental.id}
              />
            </TabsContent>
            
            <TabsContent value="subscriptions" className="m-0 p-4">
              <RentalSubscriptionsTab 
                rentalId={rental.id}
                onUpdate={handleTabUpdate}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
